
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

interface ForgotPasswordFormProps {
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
}

export const ForgotPasswordForm = ({ isSubmitting, setIsSubmitting }: ForgotPasswordFormProps) => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const { toast } = useToast();

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return "L'email est requis";
    if (!emailRegex.test(email)) return "Format d'email invalide";
    return "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      
      // Validation
      const error = validateEmail(email);
      setEmailError(error);
      if (error) {
        setIsSubmitting(false);
        return;
      }

      // Nettoyage des données
      const sanitizedEmail = email.trim().toLowerCase();
      
      // Envoi de l'email de réinitialisation
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(sanitizedEmail, {
        redirectTo: window.location.origin + '/auth/reset',
      });

      if (resetError) throw resetError;
      
      // Afficher un message de succès
      setEmailSent(true);
      toast({
        title: "Email envoyé",
        description: "Vérifiez votre boîte de réception pour réinitialiser votre mot de passe.",
      });
      
    } catch (error: any) {
      console.error("Erreur lors de la demande de réinitialisation:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Une erreur s'est produite lors de la demande de réinitialisation du mot de passe",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {emailSent ? (
        <div className="text-center space-y-4">
          <div className="text-green-500 text-3xl mb-2">✓</div>
          <h3 className="text-lg font-medium">Email envoyé!</h3>
          <p className="text-gray-600">
            Nous avons envoyé un lien de réinitialisation à <strong>{email}</strong>. 
            Veuillez vérifier votre boîte de réception (et vos spams) pour réinitialiser votre mot de passe.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <p className="text-gray-600 mb-4">
              Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
            </p>
            <Label htmlFor="reset-email">Email</Label>
            <Input
              id="reset-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError("");
              }}
              className={emailError ? "border-red-500" : ""}
              required
              disabled={isSubmitting}
            />
            {emailError && (
              <p className="text-red-500 text-sm">{emailError}</p>
            )}
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? "Envoi en cours..." : "Envoyer le lien de réinitialisation"}
          </Button>
        </form>
      )}
    </div>
  );
};
